import type { ProgramSlideLayoutV1 } from './programContentSlideCanvas';

/** 기본 16:9 (height / width) */
export const LANDING_INTRO_DEFAULT_HEIGHT_RATIO = 9 / 16;

export const LANDING_INTRO_MIN_HEIGHT_RATIO = 0.22;
export const LANDING_INTRO_MAX_HEIGHT_RATIO = 1.25;

/** 섹션 종류 — 모두 하나의 블록 스택에 섞여 표시 */
export type LandingIntroSectionKind =
  | 'slide'
  | 'feature_like'
  | 'centered_text'
  | 'image';

export type LandingIntroImageDisplay = 'carousel' | 'grid';

export interface LandingIntroImageRef {
  path?: string | null;
  url?: string | null;
  version?: number;
  alt?: string;
}

export interface LandingIntroFeatureContent {
  title: string;
  subtitle: string;
  body: string;
  image: LandingIntroImageRef | null;
  /** 미지정 시 섹션 순서로 좌우 교대. true/false면 고정 */
  reverse?: boolean;
  ctaLabel?: string;
  ctaLink?: string;
}

export interface LandingIntroCenteredTextContent {
  title: string;
  subtitle: string;
  body: string;
}

export interface LandingIntroImageBlockContent {
  display: LandingIntroImageDisplay;
  images: LandingIntroImageRef[];
  /** grid일 때 열 수 */
  columns?: 2 | 3;
}

/** slide 종류는 빈 객체, 나머지는 타입별 필드 */
export type LandingIntroBlockContent =
  | LandingIntroFeatureContent
  | LandingIntroCenteredTextContent
  | LandingIntroImageBlockContent
  | Record<string, never>;

export interface LandingIntroSection {
  id: string;
  sort_order: number;
  /** DB 호환용 — UI·공개 페이지에서 사용하지 않음 */
  title: string;
  section_kind: LandingIntroSectionKind;
  layout_json: ProgramSlideLayoutV1;
  content_json: LandingIntroBlockContent;
  /** 캔버스 높이 ÷ 너비 (slide 전용) */
  height_ratio: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface LandingIntroDraft {
  id: string;
  section_kind: LandingIntroSectionKind;
  layout_json: ProgramSlideLayoutV1;
  content_json: LandingIntroBlockContent;
  height_ratio: number;
  is_active: boolean;
  /** 아직 DB에 없는 신규 항목 */
  isNew?: boolean;
}

export function clampLandingIntroHeightRatio(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return LANDING_INTRO_DEFAULT_HEIGHT_RATIO;
  return Math.min(
    LANDING_INTRO_MAX_HEIGHT_RATIO,
    Math.max(LANDING_INTRO_MIN_HEIGHT_RATIO, n),
  );
}

export function emptyFeatureContent(): LandingIntroFeatureContent {
  return { title: '', subtitle: '', body: '', image: null };
}

export function emptyCenteredTextContent(): LandingIntroCenteredTextContent {
  return { title: '', subtitle: '', body: '' };
}

export function emptyImageBlockContent(): LandingIntroImageBlockContent {
  return { display: 'carousel', images: [], columns: 2 };
}

export function emptyContentForKind(kind: LandingIntroSectionKind): LandingIntroBlockContent {
  if (kind === 'feature_like') return emptyFeatureContent();
  if (kind === 'centered_text') return emptyCenteredTextContent();
  if (kind === 'image') return emptyImageBlockContent();
  return {} as Record<string, never>;
}

export function parseIntroSectionKind(raw: unknown): LandingIntroSectionKind {
  if (
    raw === 'feature_like' ||
    raw === 'centered_text' ||
    raw === 'image' ||
    raw === 'slide'
  ) {
    return raw;
  }
  return 'slide';
}

function asImageRef(raw: unknown): LandingIntroImageRef | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const url = typeof o.url === 'string' ? o.url : null;
  const path = typeof o.path === 'string' ? o.path : null;
  if (!url && !path) return null;
  return {
    url,
    path,
    version: typeof o.version === 'number' ? o.version : 1,
    alt: typeof o.alt === 'string' ? o.alt : '',
  };
}

function asContentObject(raw: unknown): Record<string, unknown> {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return {};
}

export function parseIntroBlockContent(
  kind: LandingIntroSectionKind,
  raw: unknown,
): LandingIntroBlockContent {
  const o = asContentObject(raw);
  if (kind === 'feature_like') {
    return {
      title: typeof o.title === 'string' ? o.title : '',
      subtitle: typeof o.subtitle === 'string' ? o.subtitle : '',
      body: typeof o.body === 'string' ? o.body : '',
      image: asImageRef(o.image),
      reverse: typeof o.reverse === 'boolean' ? o.reverse : undefined,
      ctaLabel: typeof o.ctaLabel === 'string' ? o.ctaLabel : '',
      ctaLink: typeof o.ctaLink === 'string' ? o.ctaLink : '',
    };
  }
  if (kind === 'centered_text') {
    return {
      title: typeof o.title === 'string' ? o.title : '',
      subtitle: typeof o.subtitle === 'string' ? o.subtitle : '',
      body: typeof o.body === 'string' ? o.body : '',
    };
  }
  if (kind === 'image') {
    const imagesRaw = Array.isArray(o.images) ? o.images : [];
    const images = imagesRaw
      .map((img) => asImageRef(img))
      .filter((img): img is LandingIntroImageRef => Boolean(img));
    const columns = o.columns === 3 ? 3 : 2;
    return {
      display: o.display === 'grid' ? 'grid' : 'carousel',
      images,
      columns,
    };
  }
  return {} as Record<string, never>;
}

export function introImageSrc(img: LandingIntroImageRef | null | undefined): string | null {
  if (!img?.url) return null;
  const v = img.version ?? 1;
  return `${img.url}${img.url.includes('?') ? '&' : '?'}v=${v}`;
}
